/* Draggable, minimizable floating wrapper for <DoorbellCard />.
 *
 * Fixes two real-world issues reported on actual devices:
 *  1. On iPad the card sat on top of the speed/info card with no way to
 *     move it. Now it's draggable via the header (pointer events → works
 *     on mouse + touch + pen) and remembers its position across reloads.
 *  2. On Android phones the card was hidden entirely (`hidden md:block`).
 *     It now renders on every viewport. On small screens it starts
 *     minimized to a small bell FAB so it never blocks the map by
 *     default — the user can tap to expand and drag wherever they like.
 *
 * Position + collapsed state persist in localStorage so the chosen layout
 * survives reloads. Position is clamped on every resize / drag-end so the
 * tile can never get stuck off-screen on phone rotation.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Bell, GripHorizontal, Minus, X } from "lucide-react";
import DoorbellCard from "./DoorbellCard";
import { useWebSocket } from "../../contexts/WebSocketContext";
import { useAuth } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_POS = "guard.doorbell.floating.pos";
const LS_STATE = "guard.doorbell.floating.state"; // "open" | "min" | "hidden"
const LS_SEEN_AT = "guard.doorbell.lastSeenAt";   // ISO timestamp

// Card footprint — mirrors the inner w-80 so we can clamp before mount.
const CARD_W = 320;
const CARD_H_EST = 280; // rough; clamp uses live height once measured
const FAB_SIZE = 56;
const MARGIN = 12;

function readPos() {
  try {
    const raw = localStorage.getItem(LS_POS);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p.x === "number" && typeof p.y === "number") return p;
  } catch { /* ignore */ }
  return null;
}

function clampToViewport(x, y, w, h) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  return {
    x: Math.max(MARGIN, Math.min(vw - w - MARGIN, x)),
    y: Math.max(MARGIN, Math.min(vh - h - MARGIN, y)),
  };
}

function defaultPos(w, h) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  return { x: vw - w - MARGIN, y: vh - h - MARGIN };
}

export default function FloatingDoorbell() {
  // Pick the initial state. Phones get a less intrusive minimized FAB by
  // default so the map / speed card aren't covered on first paint.
  const isPhone = typeof window !== "undefined" && window.innerWidth < 768;
  const [state, setState] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(LS_STATE) : null;
    if (saved === "open" || saved === "min" || saved === "hidden") return saved;
    return isPhone ? "min" : "open";
  });

  const [pos, setPos] = useState(() => {
    const w = state === "min" ? FAB_SIZE : CARD_W;
    const h = state === "min" ? FAB_SIZE : CARD_H_EST;
    return readPos() || defaultPos(w, h);
  });

  const containerRef = useRef(null);
  // Drag is "pending" on pointerdown and only becomes "active" once the
  // pointer has moved past a small threshold. This is the standard
  // tap-vs-drag heuristic and lets the minimize / close buttons (and the
  // FAB tap-to-expand) receive their click events instead of being
  // swallowed by an early preventDefault() on pointerdown.
  const DRAG_THRESHOLD = 6; // px
  const dragRef = useRef({ phase: "idle", dx: 0, dy: 0, sx: 0, sy: 0, moved: false, pointerId: null });

  // -- Unseen counter --------------------------------------------------
  // A ring counts as "unseen" if it started after the last time the
  // resident opened the floating tile AND was either missed or left a
  // voicemail. Answered rings never count (the resident already knows
  // about those — they answered them). Hard-capped to 99 for layout.
  const { user } = useAuth();
  const [unseen, setUnseen] = useState(0);

  const recomputeUnseen = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const r = await axios.get(`${API}/doorbell/rings?limit=20`, { withCredentials: true });
      const seenAt = localStorage.getItem(LS_SEEN_AT) || "1970-01-01T00:00:00Z";
      const rings = r.data?.rings || [];
      const count = rings.reduce((acc, ring) => {
        if (!ring.started_at || ring.started_at <= seenAt) return acc;
        // Voicemail OR un-answered → unseen. Answered calls are noise.
        const isUnseen = !!ring.voicemail_id || !ring.answered_at;
        return acc + (isUnseen ? 1 : 0);
      }, 0);
      setUnseen(Math.min(count, 99));
    } catch { /* best-effort — keep previous count */ }
  }, [user?.user_id]);

  // Initial + whenever the user identity changes.
  useEffect(() => { recomputeUnseen(); }, [recomputeUnseen]);

  // Live updates: refresh on every doorbell_end (missed/voicemail/etc.)
  // and on every doorbell_voicemail push. Scoped to this user.
  useWebSocket((msg) => {
    if (!msg || msg.user_id !== user?.user_id) return;
    if (msg.type === "doorbell_end" || msg.type === "doorbell_voicemail" || msg.type === "doorbell_ring") {
      recomputeUnseen();
    }
  });

  // Clear the badge the moment the user opens the tile (state becomes
  // "open" — either from "min" or "hidden"). We bump LS_SEEN_AT to "now"
  // so a subsequent recompute reflects the cleared baseline.
  useEffect(() => {
    if (state === "open") {
      localStorage.setItem(LS_SEEN_AT, new Date().toISOString());
      setUnseen(0);
    }
  }, [state]);

  // Persist state.
  useEffect(() => { localStorage.setItem(LS_STATE, state); }, [state]);
  useEffect(() => { localStorage.setItem(LS_POS, JSON.stringify(pos)); }, [pos]);

  // Re-clamp on viewport resize / orientation change.
  useEffect(() => {
    const onResize = () => {
      const el = containerRef.current;
      const w = el?.offsetWidth || (state === "min" ? FAB_SIZE : CARD_W);
      const h = el?.offsetHeight || (state === "min" ? FAB_SIZE : CARD_H_EST);
      setPos((p) => clampToViewport(p.x, p.y, w, h));
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    // also clamp on first mount once the real height is known
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [state]);

  const onPointerDown = useCallback((e) => {
    // Only arm a drag from the handle (data-drag-handle), never from any
    // other part of the card. CRITICALLY, do NOT preventDefault() or
    // setPointerCapture here — that would suppress click events on the
    // nested minimize / close buttons (and on the FAB itself when
    // collapsed). We only switch to "dragging" once the user has moved
    // past the threshold inside onPointerMove.
    const handle = e.target.closest("[data-drag-handle]");
    if (!handle) return;
    dragRef.current = {
      phase: "pending",
      dx: e.clientX - pos.x,
      dy: e.clientY - pos.y,
      sx: e.clientX,
      sy: e.clientY,
      moved: false,
      pointerId: e.pointerId,
    };
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (d.phase === "idle") return;
    if (d.phase === "pending") {
      const dist = Math.hypot(e.clientX - d.sx, e.clientY - d.sy);
      if (dist < DRAG_THRESHOLD) return;
      // Promote to a real drag: now we can safely prevent default + take
      // pointer capture without harming clicks (the user has clearly
      // started dragging, not tapping).
      d.phase = "dragging";
      d.moved = true;
      try { e.currentTarget.setPointerCapture?.(d.pointerId); } catch { /* ignore */ }
      try { e.preventDefault?.(); } catch { /* ignore */ }
    }
    const nx = e.clientX - d.dx;
    const ny = e.clientY - d.dy;
    const el = containerRef.current;
    const w = el?.offsetWidth || CARD_W;
    const h = el?.offsetHeight || CARD_H_EST;
    setPos(clampToViewport(nx, ny, w, h));
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current.phase = "idle";
    // Leave `moved` set for one tick so the FAB's click handler can read
    // it and suppress a stray expand() after a drag. It'll be reset on
    // the next pointerdown.
  }, []);

  const expand = () => setState("open");
  const minimize = () => setState("min");
  const hide = () => setState("hidden");

  if (state === "hidden") {
    // Provide a tiny re-open hook in the corner so the user can recover
    // the tile after closing — otherwise "close" feels like a trap.
    // Shows the unseen counter so a missed ring or voicemail isn't lost
    // when the user has closed the tile entirely.
    return (
      <button
        type="button"
        onClick={expand}
        aria-label={unseen > 0 ? `Show Scan2Call — ${unseen} unseen` : "Show Scan2Call"}
        title={unseen > 0 ? `${unseen} unseen call${unseen === 1 ? "" : "s"}` : "Show Scan2Call"}
        className="fixed bottom-3 right-3 z-30 w-9 h-9 rounded-full bg-black text-white shadow-md flex items-center justify-center opacity-60 hover:opacity-100"
        data-testid="doorbell-reopen-btn"
      >
        <Bell className="w-4 h-4" />
        <UnseenBadge count={unseen} testid="doorbell-reopen-badge" />
      </button>
    );
  }

  if (state === "min") {
    return (
      <div
        ref={containerRef}
        className="fixed z-30 select-none touch-none"
        style={{ left: pos.x, top: pos.y, width: FAB_SIZE, height: FAB_SIZE }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="doorbell-floating-min"
      >
        <button
          type="button"
          data-drag-handle
          onClick={(e) => {
            // Distinguish a drag from a tap — only expand if the pointer
            // didn't move significantly.
            if (dragRef.current.moved) { e.preventDefault(); return; }
            expand();
          }}
          aria-label={unseen > 0 ? `Expand Scan2Call — ${unseen} unseen` : "Expand Scan2Call"}
          title={unseen > 0 ? `${unseen} unseen call${unseen === 1 ? "" : "s"} — tap to expand` : "Drag to move · Tap to expand"}
          className="relative w-full h-full rounded-full bg-black text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          data-testid="doorbell-floating-min-btn"
        >
          <Bell className="w-5 h-5" />
          <UnseenBadge count={unseen} testid="doorbell-fab-badge" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed z-30 w-80 max-w-[calc(100vw-1.5rem)] select-none touch-none"
      style={{ left: pos.x, top: pos.y }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      data-testid="doorbell-floating"
    >
      <div className="bg-white border border-gray-200 rounded-sm shadow-lg overflow-hidden">
        {/* Drag handle / title bar */}
        <div
          data-drag-handle
          className="flex items-center justify-between px-2 py-1.5 bg-gray-50 border-b border-gray-200 cursor-grab active:cursor-grabbing"
          data-testid="doorbell-floating-handle"
        >
          <div className="flex items-center gap-1.5 text-gray-500 pointer-events-none">
            <GripHorizontal className="w-3.5 h-3.5" />
            <span className="text-[10px] uppercase tracking-widest font-bold font-body">Drag</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={minimize}
              aria-label="Minimize Scan2Call"
              title="Minimize"
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-sm"
              data-testid="doorbell-floating-min-toggle"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={hide}
              aria-label="Close Scan2Call"
              title="Close"
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-sm"
              data-testid="doorbell-floating-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <DoorbellCard />
      </div>
    </div>
  );
}

/* Small red circular badge with count, capped at 99+. Used on the bell
   FAB (minimized state) and the closed-state re-open pill so the
   resident can see an unseen ring even when the tile isn't expanded. */
function UnseenBadge({ count, testid }) {
  if (!count || count <= 0) return null;
  const label = count > 99 ? "99+" : String(count);
  // The size grows slightly when the label is wide ("99+") so it never
  // truncates. position-absolute relative to the parent FAB / button.
  const wide = count > 9;
  return (
    <span
      className={`absolute -top-1 -right-1 ${wide ? "min-w-[20px] h-5 px-1" : "w-5 h-5"} rounded-full bg-red-500 text-white text-[10px] font-bold font-body flex items-center justify-center border-2 border-black shadow-md leading-none`}
      data-testid={testid}
      aria-label={`${label} unseen`}
    >
      {label}
    </span>
  );
}
